import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, BookOpen, ChevronLeft, Loader2, Search } from 'lucide-react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { getMutashabihat } from '../lib/api';

const normalizeWord = (word) => word
  .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g, '')
  .replace(/[أإآٱ]/g, 'ا')
  .replace(/ى/g, 'ي')
  .replace(/[،؛,.!?؟:()[\]{}«»“”‘’—-]/g, '');

const HIGHLIGHT_COLORS = [
  {
    mark: 'bg-rose-300/70 text-rose-950 dark:bg-rose-400/40 dark:text-rose-100',
    chip: 'border-rose-300 bg-rose-50 text-rose-950 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100',
    badge: 'bg-rose-200 text-rose-900 dark:bg-rose-900 dark:text-rose-100',
  },
  {
    mark: 'bg-amber-300/70 text-amber-950 dark:bg-amber-400/40 dark:text-amber-100',
    chip: 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
    badge: 'bg-amber-200 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
  },
  {
    mark: 'bg-emerald-300/70 text-emerald-950 dark:bg-emerald-400/40 dark:text-emerald-100',
    chip: 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100',
    badge: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100',
  },
  {
    mark: 'bg-sky-300/70 text-sky-950 dark:bg-sky-400/40 dark:text-sky-100',
    chip: 'border-sky-300 bg-sky-50 text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100',
    badge: 'bg-sky-200 text-sky-900 dark:bg-sky-900 dark:text-sky-100',
  },
  {
    mark: 'bg-violet-300/70 text-violet-950 dark:bg-violet-400/40 dark:text-violet-100',
    chip: 'border-violet-300 bg-violet-50 text-violet-950 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100',
    badge: 'bg-violet-200 text-violet-900 dark:bg-violet-900 dark:text-violet-100',
  },
];

const findSharedPhrase = (ayahs, minimumWords) => {
  if (!ayahs || ayahs.length < 2) return { length: 0, phrase: '', matches: [] };
  const tokens = ayahs.map((ayah) => ayah.text.trim().split(/\s+/).map((word) => ({
    text: word,
    normalized: normalizeWord(word),
  })));
  let bestPhrase = [];
  for (let left = 0; left < tokens.length; left += 1) {
    for (let right = left + 1; right < tokens.length; right += 1) {
      const seed = tokens[left].map((token) => token.normalized);
      for (let length = seed.length; length > bestPhrase.length; length -= 1) {
        for (let start = 0; start <= seed.length - length; start += 1) {
          const candidate = seed.slice(start, start + length);
          if (candidate.some((word) => !word)) continue;
          for (let index = 0; index <= tokens[right].length - length; index += 1) {
            if (candidate.every((word, offset) => word === tokens[right][index + offset].normalized)) {
              bestPhrase = candidate;
              break;
            }
          }
          if (bestPhrase.length === length) break;
        }
        if (bestPhrase.length === length) break;
      }
    }
  }
  if (bestPhrase.length < minimumWords) {
    return { length: 0, phrase: '', matches: ayahs.map(() => []) };
  }

  // Keep exact word positions so rendering never colors an isolated repeat of a shared word.
  const matches = tokens.map((verse) => {
    const starts = [];
    for (let start = 0; start <= verse.length - bestPhrase.length; start += 1) {
      if (bestPhrase.every((word, offset) => word === verse[start + offset].normalized)) {
        starts.push(start);
      }
    }
    return starts;
  });

  let phraseTokens = [];
  for (const verse of tokens) {
    const start = verse.findIndex((token, index) =>
      index <= verse.length - bestPhrase.length
      && bestPhrase.every((word, offset) => word === verse[index + offset].normalized)
    );
    if (start >= 0) {
      phraseTokens = verse.slice(start, start + bestPhrase.length).map((token) => token.text);
      break;
    }
  }
  return { length: bestPhrase.length, phrase: phraseTokens.join(' '), matches };
};

const renderAyahText = (text, matchStarts, matchLength, markClass) => {
  let wordIndex = 0;
  return text.trim().split(/(\s+)/).map((part, index) => {
    if (!part.trim()) return part;
    const currentWordIndex = wordIndex;
    wordIndex += 1;
    const inSharedPhrase = matchStarts.some((start) =>
      currentWordIndex >= start && currentWordIndex < start + matchLength
    );
    if (!inSharedPhrase) return part;
    return (
      <mark key={index} className={`rounded-md px-1 ${markClass}`}>
        {part}
      </mark>
    );
  });
};

export default function MutashabihatPage({ onBack }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [minimumWords, setMinimumWords] = useState(3);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null);

  const loadMutashabihat = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await getMutashabihat();
      setData(response.data);
    } catch (requestError) {
      console.error('Failed to load Qalun mutashabihat:', requestError);
      setError(t('mutashabihatLoadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadMutashabihat();
  }, [loadMutashabihat]);

  const pages = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    const sourcePages = data?.pages || [];
    return sourcePages.map((page) => {
      const uniqueGroups = new Map();
      (page.groups || []).forEach((group) => {
        const verseKey = group.ayahs
          .map((ayah) => `${ayah.surah_number}:${ayah.ayah_number}`)
          .sort()
          .join('|');
        if (!uniqueGroups.has(verseKey)) uniqueGroups.set(verseKey, group);
      });
      return {
        ...page,
        groups: Array.from(uniqueGroups.values())
        .map((group) => ({
          ...group,
          sharedMatch: findSharedPhrase(group.ayahs, minimumWords),
        }))
        .filter((group) => group.sharedMatch.length >= minimumWords)
        .filter((group) => {
          if (!query) return true;
          const searchable = [
            ...(group.notes || []),
            ...group.ayahs.map((ayah) => `${ayah.surah} ${ayah.surah_number} ${ayah.ayah_number} ${ayah.text}`),
          ].join(' ').toLocaleLowerCase();
          return searchable.includes(query);
        }),
      };
    }).filter((page) => page.groups.length > 0);
  }, [data, minimumWords, searchQuery]);

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <Header />
      <main className="container mx-auto max-w-5xl px-6 py-8 pb-32">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="text-right">
            <h1 className="font-arabic text-2xl font-semibold text-foreground md:text-3xl">
              {t('mutashabihatTitle')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('qalunNarration')}</p>
          </div>
          <Button variant="outline" onClick={onBack} className="shrink-0 gap-2 rounded-full">
            <ArrowRight className="h-4 w-4" />
            <span>{t('surahs')}</span>
          </Button>
        </div>

        {data && (
          <section className="mb-6 rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-right">
                <p className="font-medium text-foreground">{t('similarityThreshold')}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t('sharedPhraseHint')}</p>
              </div>
              <span className="min-w-10 rounded-full bg-amber-500/15 px-3 py-1 text-center font-semibold text-amber-700 dark:text-amber-300">
                {minimumWords}
              </span>
            </div>
            <input
              aria-label={t('similarityThreshold')}
              type="range"
              min="2"
              max="8"
              step="1"
              value={minimumWords}
              onChange={(event) => setMinimumWords(Number(event.target.value))}
              className="w-full accent-amber-500"
            />
            <p className="mt-3 text-sm text-muted-foreground">
              {t('savedProgressPage')}: {data.progress_page} / {data.max_page}
            </p>
          </section>
        )}

        <div className="mb-5 flex items-center gap-2 rounded-xl bg-emerald-950/30 px-4 py-3 text-sm text-muted-foreground">
          <BookOpen className="h-4 w-4 shrink-0 text-amber-500" />
          <span>
            {t('dataSource')}:{' '}
            {data?.source?.name || 'الموسوعة القرآنية'} — {data?.source?.mushaf || 'مصحف الأوقاف الليبية برواية قالون'}
          </span>
        </div>

        {data && data.groups.length > 0 && (
          <div className="mb-5">
            <div className="relative max-w-lg">
              <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('searchMutashabihat')}
                className="rounded-full bg-card pr-10"
                aria-label={t('mutashabihatTitle')}
              />
            </div>
          </div>
        )}

        {error && (
          <div role="alert" className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
            <p className="mb-4 text-foreground">{error}</p>
            <Button variant="outline" onClick={loadMutashabihat} className="rounded-full">
              {t('retry')}
            </Button>
          </div>
        )}

        {isLoading && !data ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : data && !error && (
          <>
            {pages.length === 0 ? (
              <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground">
                {searchQuery ? t('noMutashabihatMatch') : t('noMutashabihatAtThreshold')}
              </div>
            ) : (
              <div className="space-y-3">
                {pages.map((page, pageIndex) => (
                  <details
                    key={page.page_number}
                    open={pageIndex === 0}
                    className="group rounded-2xl border border-border bg-card shadow-sm"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl p-5 hover:bg-muted/40">
                      <span className="font-semibold text-foreground">
                        {t('page')} {page.page_number}
                        <span className="mr-2 text-sm font-normal text-muted-foreground">
                          · {page.groups.length} {t('pageMutashabihat')}
                        </span>
                      </span>
                      <span className="text-sm text-amber-700 dark:text-amber-300">
                        {page.page_number === data.progress_page ? t('latestStudiedPage') : ''}
                      </span>
                    </summary>
                    <div className="space-y-4 border-t border-border p-4 md:p-5">
                      <div className="grid gap-3 sm:grid-cols-2">
                        {page.groups.map((group, groupIndex) => {
                          const color = HIGHLIGHT_COLORS[(groupIndex + page.page_number) % HIGHLIGHT_COLORS.length];
                          return (
                            <button
                              key={group.id}
                              type="button"
                              onClick={() => setSelectedMatch({ group, pageNumber: page.page_number, color })}
                              aria-label={`${t('viewMatchDetails')}: ${group.sharedMatch.phrase}`}
                              className={`rounded-2xl border p-4 text-right shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${color.chip}`}
                            >
                              <span className="flex items-center justify-between gap-3">
                                <span className={`flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-bold ${color.badge}`}>
                                  {groupIndex + 1}
                                </span>
                                <span className="text-xs opacity-75">{group.ayahs.length} {t('ayah')}</span>
                              </span>
                              <span className="mt-3 block font-arabic text-xl font-semibold leading-[1.9]" dir="rtl">
                                {renderAyahText(
                                  group.sharedMatch.phrase,
                                  group.sharedMatch.matches[0] || [],
                                  group.sharedMatch.length,
                                  color.mark,
                                )}
                              </span>
                              <span className="mt-3 flex items-center justify-between gap-2 border-t border-current/10 pt-3 text-xs opacity-75">
                                <span dir="rtl">
                                  {group.ayahs.slice(0, 3).map((ayah) => `${ayah.surah} ${ayah.ayah_number}`).join(' · ')}
                                  {group.ayahs.length > 3 ? ' …' : ''}
                                </span>
                                <span className="inline-flex shrink-0 items-center gap-1">
                                  {t('viewMatchDetails')} <ChevronLeft className="h-3 w-3" />
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </>
        )}

        <Dialog
          open={Boolean(selectedMatch)}
          onOpenChange={(open) => { if (!open) setSelectedMatch(null); }}
        >
          {selectedMatch && (
            <DialogContent className="max-h-[82vh] overflow-y-auto rounded-t-3xl sm:max-w-2xl sm:rounded-2xl" dir="rtl">
              <DialogHeader className="pl-8 text-right">
                <DialogTitle className="font-arabic text-2xl leading-relaxed">
                  <span className={`rounded-lg px-2 py-1 ${selectedMatch.color.mark}`}>
                    {selectedMatch.group.sharedMatch.phrase}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  {t('page')} {selectedMatch.pageNumber} · {selectedMatch.group.ayahs.length} {t('matchingAyahs')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {selectedMatch.group.ayahs.map((ayah, ayahIndex) => (
                  <article
                    key={`${ayah.surah_number}:${ayah.ayah_number}`}
                    className="rounded-2xl border border-border bg-muted/30 p-4"
                  >
                    <p className="mb-3 flex items-center justify-between gap-3 text-sm font-medium text-muted-foreground">
                      <span>{ayah.surah}</span>
                      <span>{t('ayah')} {ayah.ayah_number} · {t('page')} {ayah.page_number}</span>
                    </p>
                    <p className="font-arabic text-xl leading-[2.2] text-foreground" dir="rtl">
                      {renderAyahText(
                        ayah.text,
                        selectedMatch.group.sharedMatch.matches[ayahIndex] || [],
                        selectedMatch.group.sharedMatch.length,
                        selectedMatch.color.mark,
                      )}
                    </p>
                  </article>
                ))}
                {selectedMatch.group.notes?.length > 0 && (
                  <div className="border-t border-border pt-3">
                    {selectedMatch.group.notes.map((note, noteIndex) => (
                      <p key={noteIndex} className="text-sm leading-7 text-muted-foreground" dir="rtl">
                        {note}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          )}
        </Dialog>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          <a href="https://quranpedia.net" target="_blank" rel="noreferrer" className="underline underline-offset-4">
            quranpedia.net
          </a>
          {' · '}{data?.source?.version || ''}
        </p>
      </main>
    </div>
  );
}
